import { useState } from 'react';
import { IconBrandGoogleDrive, IconX } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { api, ApiError } from '../lib/api';
import { toast } from '../stores/toaster';

/**
 * Composant générique : prend une URL d'export (DOCX/PDF), permet à
 * l'avocat de pousser le résultat vers Drive dans la sous-farde de
 * son choix. Utilisé sur les conclusions / actes / briefings / mémos /
 * mini-dossiers.
 *
 * Pipeline :
 * 1. fetchBlob(exportPath) — récupère le fichier sans le télécharger côté browser
 * 2. POST /dossiers/<id>/gdrive/push-bytes (multipart) avec le blob
 *
 * Le sous-folder par défaut est PROCEDURE pour les conclusions/actes/jugements
 * mais peut être surchargé.
 */

type PushSubfolder = 'PROCEDURE' | 'PIECESCL' | 'PIECESADV' | 'COURRIERS' | 'EXPERTISE';

type Props = {
  dossierId: string;
  /** Path API à fetch pour récupérer le fichier export (ex: /conclusions/<id>/export.docx) */
  exportPath: string;
  /** Nom de fichier par défaut sur Drive (sans extension obligatoire) */
  defaultFilename: string;
  /** Sous-folder Drive par défaut (PROCEDURE pour conclusions/actes) */
  defaultSubfolder?: PushSubfolder;
  /** Label du bouton (défaut : "Enregistrer vers Drive") */
  label?: string;
  /** Variante d'affichage : 'icon' ou 'full' (avec label). Défaut full. */
  variant?: 'icon' | 'full';
};

const SUBFOLDER_OPTIONS: { value: PushSubfolder; label: string }[] = [
  { value: 'PROCEDURE', label: 'PROCEDURE (conclusions, actes, jugements)' },
  { value: 'PIECESCL', label: 'PIECESCL (pièces client)' },
  { value: 'PIECESADV', label: 'PIECESADV (pièces adverses)' },
  { value: 'COURRIERS', label: 'COURRIERS' },
  { value: 'EXPERTISE', label: 'EXPERTISE' },
];

export default function SendExportToDriveBtn({
  dossierId,
  exportPath,
  defaultFilename,
  defaultSubfolder = 'PROCEDURE',
  label = 'Vers Drive',
  variant = 'full',
}: Props) {
  const [open, setOpen] = useState(false);
  const [subfolder, setSubfolder] = useState<PushSubfolder>(defaultSubfolder);
  const [filename, setFilename] = useState(defaultFilename);
  const [pushing, setPushing] = useState(false);

  const submit = async () => {
    setPushing(true);
    try {
      // 1. Fetch le blob d'export (sans téléchargement browser)
      const { blob, contentType } = await api.fetchBlob(exportPath);
      // 2. Push vers Drive
      const form = new FormData();
      form.append('file', new File([blob], filename, { type: contentType }));
      form.append('subfolder', subfolder);
      form.append('filename', filename);
      const result = await api.postForm<{
        drive_file_id: string;
        drive_file_name: string;
        ref_forlex: string;
        subfolder: string;
      }>(`/dossiers/${dossierId}/gdrive/push-bytes`, form);
      toast.success(
        `Enregistré dans Drive : ${result.ref_forlex}/${result.subfolder}/${result.drive_file_name}`,
      );
      setOpen(false);
    } catch (e) {
      const err = e as ApiError;
      const msg = err?.message || err?.problem?.detail || err?.problem?.title || 'inconnu';
      if (err?.status === 503) {
        toast.error('Drive non configuré côté backend.');
      } else if (err?.status === 404) {
        toast.error('Dossier introuvable dans Drive : ' + msg);
      } else {
        toast.error('Échec push : ' + msg);
      }
    } finally {
      setPushing(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
          title="Enregistrer vers Drive"
        >
          <IconBrandGoogleDrive size={14} stroke={1.6} />
        </button>
      ) : (
        <Btn variant="ghost" size="sm" onClick={() => setOpen(true)} title="Enregistrer vers Drive">
          <IconBrandGoogleDrive size={14} stroke={1.6} className="mr-1" />
          {label}
        </Btn>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !pushing && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-md bg-paper-elevated shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between border-b border-rule px-5 py-3">
              <div>
                <h2 className="font-serif text-[16px] font-medium text-text-primary">
                  Enregistrer vers Google Drive
                </h2>
                <p className="mt-0.5 text-[11px] text-text-secondary">
                  Le fichier sera généré puis poussé dans la sous-farde Drive
                  du dossier.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !pushing && setOpen(false)}
                className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
                aria-label="Fermer"
                disabled={pushing}
              >
                <IconX size={16} stroke={1.6} />
              </button>
            </header>

            <div className="space-y-3 px-5 py-4">
              <label className="block text-[12px]">
                <span className="text-text-secondary">Sous-folder Drive cible</span>
                <select
                  value={subfolder}
                  onChange={(e) => setSubfolder(e.target.value as PushSubfolder)}
                  className="mt-1 w-full rounded border border-rule bg-paper-card px-2 py-1.5 text-[13px]"
                  disabled={pushing}
                >
                  {SUBFOLDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-[12px]">
                <span className="text-text-secondary">Nom de fichier sur Drive</span>
                <input
                  type="text"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  className="mt-1 w-full rounded border border-rule bg-paper-card px-2 py-1.5 text-[13px]"
                  disabled={pushing}
                />
              </label>

              <p className="rounded border border-rule-strong/40 bg-paper-card px-3 py-2 text-[11px] text-text-tertiary">
                La sous-farde est créée si manquante. Le folder client ne sera{' '}
                <strong>jamais</strong> créé automatiquement (règle cabinet).
              </p>
            </div>

            <footer className="flex justify-end gap-2 border-t border-rule px-5 py-3">
              <Btn variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={pushing}>
                Annuler
              </Btn>
              <Btn variant="primary" size="sm" onClick={() => void submit()} disabled={pushing}>
                {pushing ? 'Envoi…' : 'Enregistrer vers Drive'}
              </Btn>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
